"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BUS_LOAN_LEVEL_TABLE = exports.BUS_LOAN_LEVEL_GLUE_SCHEMA = exports.BUS_LOAN_LEVEL_TAPE_COLUMNS = exports.BusLoanLevelTapeRowSchema = void 0;
const zod_1 = require("zod");
const Utils_1 = require("./Utils");
/**
 * Backup Servicing (BUS) loan-level datalake table — the one cross-deal table
 * strats read (`bus_loan_level` + `bus_loan_level_latest`). Design:
 * `docs/bus/reference/portfolio-metrics.md#datalake-table`.
 *
 * The schema is fixed HERE, not derived from each tape: every facility shares
 * the table, so a tape must never change its columns. A tape is projected onto
 * these columns; one that lacks an optional column writes null.
 *
 * Every tape column is lenient (blank / unparseable → null): loan-level strats
 * are a reporting extra and must never fail a run across ~100 BUS clients. The
 * caller counts the cells that were present but unparseable, so a mapping that
 * drops a whole column is visible rather than silent.
 */
const isBlank = (value) => value === null || value === undefined || value === '';
const lenientString = zod_1.z
    .union([zod_1.z.string(), zod_1.z.number()])
    .nullish()
    .transform((value) => isBlank(value) ? null : String(value));
const lenientNumber = zod_1.z
    .union([zod_1.z.number(), zod_1.z.string()])
    .nullish()
    .transform((value) => {
    if (isBlank(value)) {
        return null;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
});
/** A `YYYY-MM-DD` date; anything else (incl. an Excel serial) is null. */
const lenientDate = zod_1.z
    .union([zod_1.z.string(), zod_1.z.number()])
    .nullish()
    .transform((value) => {
    const parsed = Utils_1.ZodFullDateStrStruct.safeParse(value);
    return parsed.success ? parsed.data : null;
});
/**
 * The tape columns the table carries, under the tape's canonical names (the
 * same names `portfolioMetrics` reads). PII — tax id, bank account / routing,
 * phone, email, street — is deliberately absent and must stay so.
 */
exports.BusLoanLevelTapeRowSchema = zod_1.z.object({
    as_of_date: lenientDate,
    contract_no: lenientString,
    customer_no: lenientString,
    customer_name: lenientString,
    original_note_amt: lenientNumber,
    current_principal_bal: lenientNumber,
    term: lenientNumber,
    remaining_term: lenientNumber,
    commencement_date: lenientDate,
    maturity_date: lenientDate,
    num_days_delin: lenientNumber,
    charge_off_amt: lenientNumber,
    closed_date: lenientDate,
    non_accrual_date: lenientDate,
    charge_off_date: lenientDate,
    loan_type_code: lenientString,
    naics_code: lenientString,
    billing_state: lenientString,
});
/** The tape columns, typed — for walking a row column by column. */
exports.BUS_LOAN_LEVEL_TAPE_COLUMNS = exports.BusLoanLevelTapeRowSchema.keyof()
    .options;
/**
 * Column → Glue type. Typed against `BusLoanLevelRow`, so a column missing
 * here, or a Glue type that disagrees with the column's value type, is a
 * compile error. Never change a type: the upload asserts against it.
 */
const BUS_LOAN_LEVEL_GLUE_TYPES = {
    credit_facility_slug: 'string',
    borrower_slug: 'string',
    lender_slug: 'string',
    as_of_date: 'date',
    contract_no: 'string',
    customer_no: 'string',
    customer_name: 'string',
    original_note_amt: 'double',
    current_principal_bal: 'double',
    term: 'double',
    remaining_term: 'double',
    commencement_date: 'date',
    maturity_date: 'date',
    num_days_delin: 'double',
    delinquency_bucket: 'string',
    charge_off_amt: 'double',
    closed_date: 'date',
    non_accrual_date: 'date',
    charge_off_date: 'date',
    loan_type_code: 'string',
    naics_code: 'string',
    billing_state: 'string',
};
/**
 * Returns `order` unchanged, but only compiles when it lists every column: a
 * forgotten one turns the parameter into `{ missingColumns: <name> }`.
 */
const everyColumnOrdered = (order) => order;
/** The table's Glue columns, in table order. Append new columns at the end. */
exports.BUS_LOAN_LEVEL_GLUE_SCHEMA = everyColumnOrdered([
    'credit_facility_slug',
    'borrower_slug',
    'lender_slug',
    'as_of_date',
    'contract_no',
    'customer_no',
    'customer_name',
    'original_note_amt',
    'current_principal_bal',
    'term',
    'remaining_term',
    'commencement_date',
    'maturity_date',
    'num_days_delin',
    'delinquency_bucket',
    'charge_off_amt',
    'closed_date',
    'non_accrual_date',
    'charge_off_date',
    'loan_type_code',
    'naics_code',
    'billing_state',
]).map((Name) => ({
    Name,
    Type: BUS_LOAN_LEVEL_GLUE_TYPES[Name],
}));
/**
 * The shared table these rows are written to (`bus_loan_level` and its
 * `_latest`, the one strats read). A literal, not `SharedTable`, so the
 * registry resolves it to `BusLoanLevelRow`.
 */
exports.BUS_LOAN_LEVEL_TABLE = 'bus_loan_level';
//# sourceMappingURL=BusLoanLevel.js.map